import { useState } from 'react';
import { Link } from 'react-router-dom';

import { requestRecovery } from '../lib/recovery/recoveryApi';
import './recovery.css';

/**
 * "I have lost my phone" — reached from the sign-in screen, by someone who
 * cannot sign in.
 *
 * Two paths, and the order matters. The kit is offered first because it is
 * instant and needs nobody's permission; the three-day route is second
 * because it is the fallback. Leading with the slow one would send people
 * down it who did not need to go.
 *
 * The copy is honest about the wait and about why it exists, rather than
 * apologising for it. Someone who understands that the delay is what stops a
 * relative walking into their vault will tolerate it; someone told only "for
 * security reasons" will phone the advisor and demand an override.
 */
export default function RecoverScreen() {
  const [email, setEmail] = useState('');
  const [sent, setSent] = useState(false);
  const [busy, setBusy] = useState(false);

  async function start() {
    if (email.trim() === '') return;
    setBusy(true);
    try {
      await requestRecovery(email.trim());
    } catch {
      // Deliberately swallowed. This screen shows the same thing whatever
      // happened, because whether an address has a vault is not something a
      // stranger should be able to learn here.
    } finally {
      setBusy(false);
      setSent(true);
    }
  }

  if (sent) {
    return (
      <section className="auth recovery">
        <p className="vault__kicker">We have started</p>
        <h1 className="vault__title">Check your email</h1>
        <p className="vault__sub">
          If that address has a vault, we have emailed you — and told your advisor and your
          guardians that someone is trying to get back in.
        </p>
        <p className="vault__sub">
          In three days you will be able to set up this device. Nothing in your vault changes in the
          meantime, and nothing is delivered.
        </p>
        <p className="vault__sub">
          If this was not you, the email has a link that stops it immediately. You do not need a
          password to use it.
        </p>
        <p className="recovery__back"><Link to="/signin">Back to sign in</Link></p>
      </section>
    );
  }

  return (
    <section className="auth recovery">
      <p className="vault__kicker">Lost your phone</p>
      <h1 className="vault__title">Getting you back in</h1>

      <div className="la-card recovery__card">
        <p className="vault__section-label">First, try signing in</p>
        <p className="vault__sub">
          If your new phone is signed in to the same Apple or Google account as your old one, your
          passkey usually travels with you. Most people get back in this way without needing
          anything below.
        </p>
        <Link className="la-btn-primary" to="/signin">Try signing in</Link>
      </div>

      <div className="la-card recovery__card">
        <p className="vault__section-label">If that works, and your key did not travel</p>
        <p className="vault__sub">
          Sometimes you can sign in but this device cannot open your replies or recordings. That is
          what your Recovery Kit is for — the twenty-four characters you were told to keep
          somewhere safe.
        </p>
        <p className="vault__sub recovery__why">
          The kit restores your key onto a device you are already signed in to. It cannot sign you
          in on its own, so if you cannot get in at all, use the option below first.
        </p>
        <Link className="la-btn-secondary" to="/recover/kit">Enter my kit</Link>
      </div>

      <div className="la-card recovery__card">
        <p className="vault__section-label">If you cannot sign in at all</p>
        <p className="vault__sub">
          We can still get you back in. It takes three days, and while we wait we tell you, your
          advisor and your guardians that someone has asked — so that if it is not you, any one of
          them can stop it with a single click.
        </p>
        <p className="vault__sub recovery__why">
          The wait is not a formality. It is the thing that stops someone who knows you well enough
          to be convincing from walking into your vault quietly.
        </p>
        <label className="auth__field">
          <span className="auth__label">Your email address</span>
          <input
            className="auth__input"
            type="email"
            autoComplete="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
          />
        </label>
        <button type="button" className="la-btn-secondary" onClick={() => void start()} disabled={busy || email.trim() === ''}>
          {busy ? 'Starting…' : 'Start the three-day recovery'}
        </button>
      </div>

      <p className="vault__sub recovery__reassure">
        Either way, your messages are safe. They are not stored on your phone, and losing it has
        never put a single one at risk.
      </p>
      <p className="recovery__back"><Link to="/signin">Back to sign in</Link></p>
    </section>
  );
}
